import { OFFER_TIMEOUT_DEFAULT_S, OFFER_TIMEOUT_MAX_S, OFFER_TIMEOUT_MIN_S } from "@sagot/constants";

/** How often the console asks for the member's open offers when no event arrives (an offer outlives a closed stream). */
export const OFFER_POLL_MS = 10_000;
/** The countdown redraws this often. */
export const OFFER_TICK_MS = 1_000;
/** An `offer_created` event is followed by a second refetch this much later, in case the first beat the commit. */
export const OFFER_REFETCH_AGAIN_MS = 1_000;

/** Inbox events that can change which offers a member holds. */
const OFFER_EVENT_TYPES: ReadonlySet<string> = new Set(["offer_created", "offer_expired", "assigned", "updated"]);

export function isOfferEvent(type: string): boolean {
  return OFFER_EVENT_TYPES.has(type);
}

/** True when an event concerns the signed-in user: the offer events name who the offer is for, the others concern everyone. */
export function concerns(event: { type: string; data?: Record<string, unknown> | undefined }, userId: string | undefined): boolean {
  if (event.type !== "offer_created" && event.type !== "offer_expired") return true;
  return userId !== undefined && event.data?.assignee_id === userId;
}

/** Whole seconds left on an offer: `offsetMs` is the server's clock minus the console's, read when the offers were fetched. */
export function secondsLeft(expiresAt: string, offsetMs: number, nowMs: number): number {
  const remaining = Date.parse(expiresAt) - (nowMs + offsetMs);
  return Math.max(0, Math.ceil(remaining / 1000));
}

export function totalSeconds(offeredAt: string, expiresAt: string): number {
  return Math.max(1, Math.round((Date.parse(expiresAt) - Date.parse(offeredAt)) / 1000));
}

/** Share of the time still left, 0..100. */
export function percentLeft(left: number, total: number): number {
  return Math.min(100, Math.max(0, Math.round((left / total) * 100)));
}

export const OFFER_SHORTCUTS = {
  accept: { code: "KeyA", label: "Alt+Shift+A" },
  decline: { code: "KeyD", label: "Alt+Shift+D" },
} as const;

type KeyInput = Pick<KeyboardEvent, "altKey" | "shiftKey" | "ctrlKey" | "metaKey" | "repeat" | "code">;

/** The offer action a key press asks for (`Alt+Shift+A` accepts, `Alt+Shift+D` passes), or null. Matches the physical key so layouts and Alt dead keys do not matter. */
export function offerShortcut(event: KeyInput): "accept" | "decline" | null {
  if (!event.altKey || !event.shiftKey || event.ctrlKey || event.metaKey || event.repeat) return null;
  if (event.code === OFFER_SHORTCUTS.accept.code) return "accept";
  if (event.code === OFFER_SHORTCUTS.decline.code) return "decline";
  return null;
}

export const OFFER_TIMEOUT_BOUNDS = { min: OFFER_TIMEOUT_MIN_S, max: OFFER_TIMEOUT_MAX_S, fallback: OFFER_TIMEOUT_DEFAULT_S } as const;

/** The timeout typed in the settings card as a whole number of seconds within bounds, or null. */
export function parseTimeout(raw: string): number | null {
  const text = raw.trim();
  if (!/^\d{1,4}$/.test(text)) return null;
  const value = Number(text);
  return value >= OFFER_TIMEOUT_MIN_S && value <= OFFER_TIMEOUT_MAX_S ? value : null;
}

export interface OfferSettings {
  enabled: boolean;
  timeout_s: number;
}

export interface OfferSettingsDraft {
  enabled: boolean;
  timeout: string;
}

export function draftOfSettings(settings: OfferSettings): OfferSettingsDraft {
  return { enabled: settings.enabled, timeout: String(settings.timeout_s) };
}

/** The body to save, or null while the draft is invalid or unchanged. */
export function settingsBody(saved: OfferSettings, draft: OfferSettingsDraft): OfferSettings | null {
  const timeout = parseTimeout(draft.timeout);
  if (timeout === null) return null;
  if (timeout === saved.timeout_s && draft.enabled === saved.enabled) return null;
  return { enabled: draft.enabled, timeout_s: timeout };
}
